import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, IconButton, Tooltip, TablePagination, Box, Typography,
  Tab, Tabs, CircularProgress, Alert, Snackbar,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PublishIcon from '@mui/icons-material/Publish';
import Layout from '../../components/Layout/Layout';
import PageHeader from '../../components/Common/PageHeader';
import SearchFilterBar from '../../components/Common/SearchFilterBar';
import ConfirmDialog from '../../components/Common/ConfirmDialog';
import {
  getPublisherContentApi,
  downloadPublisherContentApi,
  adminPublishContentApi,
} from '../../api/uploads';

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  window.URL.revokeObjectURL(url);
}

const STATUS_COLOR = { Pending: 'warning', Published: 'success' };

export default function PublisherContent() {
  const [tab, setTab] = useState(0); // 0=All, 1=Pending, 2=Published
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [confirmPublish, setConfirmPublish] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [message, setMessage] = useState(null);

  const statusFilter = ['', 'Pending', 'Published'][tab];

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: page + 1, page_size: rowsPerPage, search };
      if (statusFilter) params.status = statusFilter;
      const res = await getPublisherContentApi(params);
      setRows(res.data.results || res.data);
      setTotal(res.data.count || (res.data.results ? res.data.count : res.data.length));
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDownload = async (row) => {
    try {
      const res = await downloadPublisherContentApi(row.id);
      downloadBlob(res.data, row.original_file_name);
    } catch {
      // ignore
    }
  };

  const handlePublish = async () => {
    if (!confirmPublish) return;
    setPublishing(true);
    try {
      const res = await adminPublishContentApi(confirmPublish.id, {});
      const booksCreated = res.data.books_created || 0;
      setMessage({
        severity: 'success',
        text: booksCreated > 0
          ? `Published successfully. ${booksCreated} book(s) created.`
          : 'Published successfully.',
      });
      setConfirmPublish(null);
      fetchData();
    } catch (err) {
      setMessage({ severity: 'error', text: err.response?.data?.error || 'Failed to publish content.' });
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Layout title="Publisher Content">
      <PageHeader
        title="Publisher Content"
        subtitle="Review and publish uploaded files from publishers"
        breadcrumbs={[{ label: 'Admin' }, { label: 'Publisher Content' }]}
      />

      <Tabs value={tab} onChange={(_, v) => { setTab(v); setPage(0); }} sx={{ mb: 2 }}>
        <Tab label="All" />
        <Tab label="Pending Review" />
        <Tab label="Published" />
      </Tabs>

      <Card>
        <CardContent>
          <SearchFilterBar
            searchPlaceholder="Search by file name, publisher, company..."
            onSearch={(v) => { setSearch(v); setPage(0); }}
          />

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Publisher</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>File Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Content Type</TableCell>
                    <TableCell>Uploaded</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Published By</TableCell>
                    <TableCell>Books</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.publisher_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{r.publisher_email}</Typography>
                      </TableCell>
                      <TableCell>{r.publisher_company}</TableCell>
                      <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Tooltip title={r.original_file_name}>
                          <span>{r.original_file_name}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Chip label={r.file_type?.toUpperCase()} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        {r.content_type ? <Chip label={r.content_type} size="small" color="info" variant="outlined" /> : '—'}
                      </TableCell>
                      <TableCell>{new Date(r.uploaded_date).toLocaleDateString()}</TableCell>
                      <TableCell>
                        <Chip
                          label={r.status}
                          size="small"
                          color={STATUS_COLOR[r.status] || 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {r.published_by_name
                          ? <Typography variant="caption">{r.published_by_name}<br />{r.published_date ? new Date(r.published_date).toLocaleDateString() : ''}</Typography>
                          : '—'}
                      </TableCell>
                      <TableCell>{r.books_created > 0 ? r.books_created : '—'}</TableCell>
                      <TableCell>
                        <Tooltip title="Download">
                          <IconButton size="small" color="primary" onClick={() => handleDownload(r)}>
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                        {r.status === 'Pending' && (
                          <Tooltip title="Publish">
                            <IconButton size="small" color="success" onClick={() => setConfirmPublish(r)}>
                              <PublishIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">No content found.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={total}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_, p) => setPage(p)}
                onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={!!confirmPublish}
        title="Publish Content"
        message={
          confirmPublish
            ? `Publish "${confirmPublish.original_file_name}" from ${confirmPublish.publisher_name}? `
              + (['.xlsx', '.xls', '.csv'].includes(confirmPublish.file_type?.toLowerCase())
                ? 'Books will be created from this file.'
                : 'This will mark the content as published.')
            : ''
        }
        onConfirm={handlePublish}
        onCancel={() => setConfirmPublish(null)}
        confirmText={publishing ? 'Publishing...' : 'Publish'}
        confirmColor="success"
      />

      <Snackbar open={!!message} autoHideDuration={6000} onClose={() => setMessage(null)}>
        {message && <Alert severity={message.severity} onClose={() => setMessage(null)}>{message.text}</Alert>}
      </Snackbar>
    </Layout>
  );
}
